/**
 * Known measurement / engineering tokens at the end of a label or value.
 * Longer tokens first so "mm/m" wins over "m" or "mm", "kg/m3" over "m3".
 */
const ORDERED_UNIT_SUFFIXES = [
  "W/m·K",
  "W/mK",
  "W/m•K",
  "mm/m",
  "kg/m³",
  "kg/m3",
  "kg/m²",
  "kg/m2",
  "kg/m",
  "N/mm²",
  "N/mm2",
  "N/mm",
  "kN/m",
  "kN",
  "N",
  "MPa",
  "m²",
  "m2",
  "m³",
  "m3",
  "mm",
  "cm",
  "μm",
  "μs",
  "m",
  "°C",
  "%",
  "Hz",
  "Pa",
  "bar",
  "min",
  "dB",
];

const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * @param {string} rawValue
 * @param {string} [explicitUnit] — optional CMS field `unit` / `units`
 * @returns {{ valueText: string, unit: string }}
 */
export function parseSpecificationValue(rawValue, explicitUnit) {
  const raw = String(rawValue ?? "").trim();
  const ex = String(explicitUnit ?? "").trim();
  if (ex) {
    let v = raw;
    if (v.toLowerCase().endsWith(ex.toLowerCase())) {
      v = v.slice(0, v.length - ex.length).replace(/[\s,;:]+$/u, "").trim();
    }
    return { valueText: v || raw, unit: ex };
  }
  if (!raw) return { valueText: "", unit: "" };

  for (const unit of ORDERED_UNIT_SUFFIXES) {
    const re = new RegExp(`^(.+?)\\s+(${escapeRegExp(unit)})\\s*$`, "iu");
    const m = raw.match(re);
    if (m) {
      const left = m[1].replace(/[\s,;:]+$/u, "").trim();
      return { valueText: left || raw, unit: m[2] };
    }
  }

  for (const unit of ORDERED_UNIT_SUFFIXES) {
    const lower = raw.toLowerCase();
    const uLower = unit.toLowerCase();
    const idx = lower.lastIndexOf(uLower);
    if (idx >= 1 && idx + unit.length === raw.length) {
      // Avoid stripping a trailing letter from real words (e.g. the final "n"
      // in "Sound Reduction" / "Galvanization" / "Movement Accommodation"
      // must not be parsed as the unit "N"). Only accept the suffix as a unit
      // when the preceding character is not a letter.
      const prevChar = raw.charAt(idx - 1);
      if (/[\p{L}]/u.test(prevChar)) continue;
      const left = raw.slice(0, idx).trim();
      if (left) return { valueText: left, unit: raw.slice(idx) };
    }
  }

  return { valueText: raw, unit: "" };
}

/**
 * Combines label-based and value-based unit detection (CMS often puts "Thickness mm"
 * in the label and "12.5" in the value).
 * @param {{ label?: string, value?: string, unit?: string, units?: string }} item
 * @returns {{ specificationText: string, unit: string, valueText: string }}
 */
export function resolveSpecificationRowParts(item) {
  const labelRaw = String(item?.label ?? "").trim();
  const valueRaw = String(item?.value ?? "").trim();
  const explicit = String(item?.unit ?? item?.units ?? "").trim();

  const fromLabel = parseSpecificationValue(labelRaw, "");
  const fromValue = parseSpecificationValue(valueRaw, explicit);

  const unit = (fromValue.unit || fromLabel.unit || explicit || "").trim();
  const specificationText = fromLabel.unit ? fromLabel.valueText.trim() : labelRaw;
  const valueText = fromValue.valueText.trim();

  return { specificationText, unit, valueText };
}

function wordCount(text) {
  const t = String(text ?? "").trim();
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}

/**
 * Board CMS: section whose heading is only "Note" / "Notes" (case-insensitive).
 * @param {string} [heading]
 */
export function matchesNotesSectionHeading(heading) {
  return /^\s*notes?\s*:?\s*$/i.test(String(heading ?? "").trim());
}

/**
 * Accessory / flat spec rows: prose-style notes without measurement units
 * (long copy in the value field, no explicit or parsed unit, no standard reference).
 * @param {{ label?: string, value?: string, unit?: string, units?: string, reference?: string, standard?: string, astm?: string, isNote?: boolean, note?: boolean }} item
 */
export function isSpecificationNoteRow(item) {
  if (item?.isNote === true || item?.note === true) return true;
  if (item?.isNote === false) return false;

  const labelRaw = String(item?.label ?? "").trim();
  const valueRaw = String(item?.value ?? "").trim();
  const explicit = String(item?.unit ?? item?.units ?? "").trim();
  if (!valueRaw || !labelRaw) return false;
  if (explicit) return false;

  const fromLabel = parseSpecificationValue(labelRaw, "");
  if (fromLabel.unit) return false;

  const fromValue = parseSpecificationValue(valueRaw, "");
  if (fromValue.unit) return false;

  const ref = String(item?.reference ?? item?.standard ?? item?.astm ?? "").trim();
  if (ref && ref !== "—" && ref !== "-" && ref !== "–") return false;

  const wc = wordCount(valueRaw);
  const len = valueRaw.length;
  if (len <= 18 && wc <= 3) return false;

  if (len >= 90 || wc >= 14) return true;
  if (len >= 32 && wc >= 5) return true;
  if (len >= 45 && wc >= 4) return true;

  return false;
}
